import { SetStateAction, Dispatch, ChangeEvent, useState } from 'react';
import {
  DevHubTextField,
  DevHubAutocomplete,
  DevHubAutocompleteChangeReason,
  DevHubDynamicIcon,
  devhubMakeStyles,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { debounce } from 'debounce';
import { ApplicationGroup } from '../insights/types/InsightsTypes';

const AppGroupQuery = gql`
  query ApplicationGroups($filter: ResourceCatalogApplicationFilter) {
    resources {
      applicationGroups(filter: $filter) {
        name
        applicationId
      }
    }
  }
`;

const useStyles = devhubMakeStyles({
  search: {
    marginTop: '0.25rem',
  },
});

interface Props {
  className?: string | undefined;
  helperText: string;
  setAppGroup: Dispatch<SetStateAction<ApplicationGroup>>;
  selectedAppGroup?: ApplicationGroup;
  label?: string;
  required?: boolean;
}

/**
 * Application search component to grab an application group from fusion
 * @param props props of the component
 * @param props.className css style for the component
 * @param props.helperText helper text to display with the component
 */
function ApplicationGroupSearch(props: Props) {
  const [appGroups, setAppGroups] = useState([] as ApplicationGroup[]);
  const classes = useStyles();

  const [getAppGroups, { loading, error }] = useLazyQuery(AppGroupQuery, {
    errorPolicy: 'all',
  });

  const handleSearch = debounce((value: string) => {
    getAppGroups({
      variables: { filter: { applicationName: `${value}*` } },
    }).then(groupResults => {
      if (groupResults?.data?.resources?.applicationGroups?.length > 0) {
        setAppGroups(groupResults?.data?.resources?.applicationGroups);
      }
    });
  }, 500);

  // Event Handler for the text box and to trigger the query after 3 keystrokes
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length >= 3) {
      handleSearch(value);
    }
  };

  // Event Handler to set selected party from a drop-down
  const onSelect = (
    _: Object,
    value: ApplicationGroup | null,
    reason: DevHubAutocompleteChangeReason,
  ): void => {
    if (reason === 'clear') {
      props.setAppGroup({ name: 'clear' } as ApplicationGroup);
    }
    if (!value) {
      return;
    }
    props.setAppGroup(value);
  };

  return (
    <>
      {error ? (
        <DevHubTextField
          error
          label="Error"
          className={props.className}
          defaultValue="No Application Groups Available"
          helperText="Failed to fetch application group details."
          variant="outlined"
        />
      ) : (
        <DevHubAutocomplete
          // className={props.className}
          size="small"
          loading={loading}
          loadingText="Loading.."
          options={appGroups}
          onChange={onSelect}
          defaultValue={
            props?.selectedAppGroup?.name ? props?.selectedAppGroup : undefined
          }
          getOptionLabel={option => option?.name}
          renderInput={params => (
            <DevHubTextField
              {...params}
              label={props?.label ?? 'Search for an Application Group'}
              margin="normal"
              helperText={props.helperText}
              variant="outlined"
              required={props?.required ?? true}
              onChange={onChange}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <span className={classes.search}>
                      <DevHubDynamicIcon iconName="Search" />
                    </span>
                  </>
                ),
              }}
            />
          )}
        />
      )}
    </>
  );
}

export default ApplicationGroupSearch;
